import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import toast from "components/ui/toast/Toast";
import { Format } from "lib/types/graphQLEnums";
import { useSupportChat } from "lib/utils/useSupportChat";
import { ExportFormatArgs } from "../../lib/generated/app-service-gql/graphql";
import { useFileExport } from "../../lib/hooks/api/exports/useFileExport";
import { useCurrentUser } from "../../lib/hooks/api/users/useCurrentUser";
import { EventDataTypes, EventNames, getPageSource, useTracking } from "../../lib/tracking";

const DEFAULT_EXPORT_LIMIT = 500;

type RequiredExportVariables = { exportFormat: ExportFormatArgs };
type RequiredDataResult = { id: string };

/**
 * Only what this button and `useFileExport` actually read. Deliberately not react-query's
 * `UseMutationResult`: that is a union discriminated on status, so a hook narrowing `data` to
 * one field of its mutation payload cannot satisfy it without a cast.
 */
type ExportMutation<T extends RequiredDataResult, V extends RequiredExportVariables> = {
  data?: T;
  isLoading: boolean;
  reset: () => void;
  mutate: (variables: V) => void;
  variables?: V;
};

type Props<T extends RequiredDataResult, V extends RequiredExportVariables> = {
  exportMutation: ExportMutation<T, V>;
  exportVariables: Omit<V, "exportFormat">;
  totalResults: number;
  isLoadingTotalResults: boolean;
  isEnabledTotalResults?: boolean;
  exportDataType: EventDataTypes;
  exportLimit?: number;
  extraDropdownItems?: React.ReactNode;
};
// TODO: rename once this is the primary export button
export function ExportButtonGraphql<
  T extends RequiredDataResult,
  V extends RequiredExportVariables,
>({
  isLoadingTotalResults,
  isEnabledTotalResults = true,
  totalResults,
  exportMutation,
  exportVariables,
  exportDataType,
  exportLimit = DEFAULT_EXPORT_LIMIT,
  extraDropdownItems,
}: Props<T, V>) {
  const { openSupportChat } = useSupportChat();
  const { data: currentUser } = useCurrentUser();
  const { mutate, variables } = exportMutation;
  const { logEvent } = useTracking();

  const { isExporting } = useFileExport({
    mutation: exportMutation,
    onSuccess: () => {
      logEvent(EventNames.exportedData, {
        Count: totalResults,
        "Data type": exportDataType,
        Format: variables?.exportFormat?.fileFormat,
      });
    },
    // Filename fallback if the content-disposition header is missing for some reason
    getFileName: () =>
      `stotles-export-${new Date().toJSON().slice(0, 10)}.${variables?.exportFormat?.fileFormat}`,
    // Longer than the 4s default so the failure is readable before it dismisses.
    onError: () => toast.error("Export failed. Please try again later.", { duration: 10000 }),
  });

  const startExport = async (format: Format) => {
    mutate({ ...exportVariables, exportFormat: { fileFormat: format } } as V);
  };

  // TODO extract this to separate PaywallExport wrapper component or similar
  const exportLimitReached = totalResults > exportLimit;
  const hasExportPermission = currentUser?.hasExportPermission;

  const limitExport = exportLimitReached && !hasExportPermission;
  const limitExportTrigger = limitExport && !extraDropdownItems;
  const limitExportTooltip = (
    <span>
      {`Narrow results to ${exportLimit} or less to export. Need more? `}
      <br />
      <Button buttonType="tertiary" onClick={() => openSupportChat(getPageSource())}>
        Chat with our team to get started.
      </Button>
    </span>
  );

  return (
    <DropdownMenu
      width="var(--radix-dropdown-menu-trigger-width)"
      modal={false}
      trigger={
        <DropdownMenuTrigger
          buttonType="secondary"
          icon="download01"
          loading={(isLoadingTotalResults && isEnabledTotalResults) || isExporting}
          disabled={
            !isEnabledTotalResults || isLoadingTotalResults || isExporting || limitExportTrigger
          }
          tooltip={limitExportTrigger ? { content: limitExportTooltip } : undefined}
        >
          Export ({totalResults})
        </DropdownMenuTrigger>
      }
    >
      {extraDropdownItems}
      <DropdownMenuItem
        onClick={() => startExport(Format.Xlsx)}
        disabled={limitExport}
        tooltip={limitExport ? { content: limitExportTooltip } : undefined}
      >
        Export to XLSX
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => startExport(Format.Csv)}
        disabled={limitExport}
        tooltip={limitExport ? { content: limitExportTooltip } : undefined}
      >
        Export to CSV
      </DropdownMenuItem>
    </DropdownMenu>
  );
}
